import {
  addDays,
  differenceInMinutes,
  isAfter,
  isValid,
  parse,
  subDays,
} from 'date-fns';
import { millisecondsInSecond } from 'date-fns/constants';
import {
  parse as iso8601parse,
  pattern as iso8601DurationPattern,
  toSeconds,
} from 'iso8601-duration';
import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { TRIMET_TIME_ZONE } from '#/shared/constants';

// date and time formats use Unicode Technical Standard #35 for compatibility
// with `date-fns`: https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table

const MILLISECONDS_TIMESTAMP_FORMAT = 'T';

// formats required for inputs to OpenTripPlanner routing engine
const OTP_API_DATE_FORMAT = 'yyyy-MM-dd';
const OTP_API_TIME_FORMAT = 'HH:mm';

// this is an arbitrary character used to be able to parse a datetime
// string into date and time parts
const DATE_AND_TIME_SEPARATOR = '@';
const OTP_DATETIME_FORMAT =
  OTP_API_DATE_FORMAT + DATE_AND_TIME_SEPARATOR + OTP_API_TIME_FORMAT;

/**
 * Compute the reservation cut-off Date:
 *   tripTimeMs minus daysPrior days, then set to HH:mm:ss
 */
export function computeDeadline(tripTimeMs, { daysPrior, time }) {
  const [hh, mm, ss] = time.split(':').map(Number);
  const tripDate = new Date(tripTimeMs);
  const base = subDays(tripDate, daysPrior);
  base.setHours(hh, mm, ss, 0);
  return base;
}

export function formatReservationTime(date) {
  return formatInTimeZone(date, TRIMET_TIME_ZONE, 'h:mm a');
}

export function formatReservationDate(date) {
  return formatInTimeZone(date, TRIMET_TIME_ZONE, 'MMMM d, yyyy');
}

// TODO - PUBWEB-3554: After converting date-time utils module to TypeScript,
// move to `#/trip-planner/utils/flex` to better categorize its content.
export function getMinimumBookingNoticeMilliseconds(bookingInfo) {
  const { minimumBookingNotice } = bookingInfo || {};
  if (typeof minimumBookingNotice !== 'string') return null;

  const value = minimumBookingNotice.trim();
  const isNegative = value.startsWith('-');
  const normalizedValue = value.replace(/^[+-]/, '');

  // The library's parser is broader than our contract, so enforce a full-string match.
  const fullIsoDurationPattern = new RegExp(
    `^${iso8601DurationPattern.source}$`
  );
  if (!fullIsoDurationPattern.test(normalizedValue)) return null;

  const parsed = iso8601parse(normalizedValue);

  const durationMs = toSeconds(parsed) * millisecondsInSecond;

  return isNegative ? -durationMs : durationMs;
}

// TODO - PUBWEB-3554: After converting date-time utils module to TypeScript,
// move to `#/trip-planner/utils/flex` to better categorize its content.
function getExpiredMinimumNoticeResponse(
  bookingInfo,
  tripTimeMs,
  currentDateTime
) {
  const noticeMs = getMinimumBookingNoticeMilliseconds(bookingInfo);
  if (noticeMs == null) return null;
  const bookingDeadline = new Date(tripTimeMs - noticeMs);
  if (currentDateTime <= bookingDeadline) return null;
  const newDateTime = new Date(currentDateTime.getTime() + noticeMs);
  return {
    messageId: 'routes.reserve-trip-expired',
    values: {
      nextAvailableTime: formatReservationTime(newDateTime),
      nextAvailableDate: formatReservationDate(newDateTime),
    },
    reservationExpired: true,
  };
}

// TODO - PRJ77-1783: Migrate away from use of bookingRuleInfo
// TODO(PUBWEB-3554): When converting this module to TypeScript move this
// function to `#/trip-planner/utils/flex` to better categorize its content
// Its tests already reside in a module adjacent to that target
/**
 * @param {import('#/types/otp-ui.extensions').BookingRuleInfo} bookingRuleInfo
 * @param {number} tripTimeMs - Trip start time in milliseconds
 * @returns {{ messageId: string, values: object, reservationExpired?: boolean }} Object with messageId for FormattedMessage and values for interpolation
 */
export function getReservationMessageData(bookingRuleInfo, tripTimeMs) {
  const { pickUp, dropOff } = bookingRuleInfo;
  const currentDateTime = new Date();
  const defaultResponse = { messageId: 'routes.reserve-trip', values: {} };

  // minimumBookingNotice: deadline = tripTime - notice. Only show expired if deadline has passed.
  const expiredNoticeResponse = [pickUp, dropOff]
    .map(place =>
      getExpiredMinimumNoticeResponse(place, tripTimeMs, currentDateTime)
    )
    .find(Boolean);

  if (expiredNoticeResponse) return expiredNoticeResponse;

  // Select data source for latestBookingTime, prioritizing pickUp over dropOff
  const primarySource = pickUp?.latestBookingTime ? pickUp : dropOff;
  const latestBookingTime = primarySource?.latestBookingTime;

  // Guard: No valid latestBookingTime found
  if (
    !latestBookingTime?.time ||
    latestBookingTime.daysPrior === null ||
    latestBookingTime.daysPrior === undefined
  ) {
    return defaultResponse;
  }

  const latestDeadline = computeDeadline(tripTimeMs, latestBookingTime);

  // latestBookingTime deadline has passed — compute next valid booking date
  if (currentDateTime > latestDeadline) {
    const { daysPrior, time } = latestBookingTime;
    const cutoffToday = parse(time, 'HH:mm:ss', currentDateTime);
    const isPastCutoff = isAfter(currentDateTime, cutoffToday);
    const newDate = addDays(
      currentDateTime,
      daysPrior + (isPastCutoff ? 1 : 0)
    );
    return {
      messageId: 'routes.reserve-trip-expired-on-or-after',
      values: {
        nextAvailableDate: formatReservationDate(newDate),
      },
      reservationExpired: true,
    };
  }

  // Booking window is still open — show before/between messages
  const earliestBookingTime = primarySource?.earliestBookingTime;

  if (earliestBookingTime) {
    if (
      !earliestBookingTime.time ||
      earliestBookingTime.daysPrior === null ||
      earliestBookingTime.daysPrior === undefined
    ) {
      return defaultResponse;
    }

    const earliestDeadline = computeDeadline(tripTimeMs, earliestBookingTime);
    return {
      messageId: 'routes.reserve-trip-between',
      values: {
        earliestTime: formatReservationTime(earliestDeadline),
        earliestDate: formatReservationDate(earliestDeadline),
        latestTime: formatReservationTime(latestDeadline),
        latestDate: formatReservationDate(latestDeadline),
      },
    };
  }

  return {
    messageId: 'routes.reserve-trip-before',
    values: {
      latestTime: formatReservationTime(latestDeadline),
      latestDate: formatReservationDate(latestDeadline),
    },
  };
}

/**
 * Get the number of minutes that the supplied timestamp is from the current
 * time, a minutes floor is returned, meaning there is no rounding and any
 * seconds in excess of the last full minute are discarded. If the current time
 * is greater than the supplied time a negative value is returned
 *
 * @param milliSecondsTimeStamp Milliseconds since the epoch
 * @returns {Number} Minutes integer
 */
export const getMinutesFromNow = milliSecondsTimeStamp =>
  differenceInMinutes(new Date(milliSecondsTimeStamp), new Date());

/**
 * Format a Date object into OTP API date and time strings in the timezone
 * that the TriMet OTP engine is running.  OTP doesn't accept any information
 * pertaining to timezone for trip requests and assumes datetime information is
 * in its timezone
 *
 * @param {Date} date
 * @returns {String}
 */
export const formatToOtpApiDateAndTimeStrings = date =>
  formatInTimeZone(date, TRIMET_TIME_ZONE, OTP_DATETIME_FORMAT).split(
    DATE_AND_TIME_SEPARATOR
  );

/** @param {string | number} milliSeconds */
export const parseMilliseconds = milliSeconds =>
  parse(milliSeconds.toString(), MILLISECONDS_TIMESTAMP_FORMAT, new Date());

/**
 * Convert text representation of date and time parts in the OTP API format to
 * a Date object. OTP engine query params don't contain timezone information and
 * the engine assumes all datetime info is in the timezone in which it's running
 *
 * @param {String} otpDateString
 * @param {String} otpTimeString
 * @returns {Date}
 */
export const parseFromOtpApiDateAndTimeStrings = (
  otpDateString,
  otpTimeString
) =>
  zonedTimeToUtc(
    parse(
      otpDateString + DATE_AND_TIME_SEPARATOR + otpTimeString,
      OTP_DATETIME_FORMAT,
      new Date()
    ),
    TRIMET_TIME_ZONE
  );

/** Converts supplied Date from the TriMet time zone to UTC  */
export const triMetTimeZoneToUtc = date =>
  zonedTimeToUtc(date, TRIMET_TIME_ZONE);

/** Converts supplied Date from UTC to the TriMet time zone  */
export const utcToTriMetTimeZone = date =>
  utcToZonedTime(date, TRIMET_TIME_ZONE);

/**
 * Get a new Date in the TriMet time zone.  If a time, in milliseconds since
 * the epoch, is not passed the current time will be used
 */
export const getNewTriMetDate = milliseconds =>
  utcToTriMetTimeZone(milliseconds ? new Date(milliseconds) : new Date());

export const userInTriMetTimeZone = () =>
  TRIMET_TIME_ZONE === Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Validates if the provided date and time strings conform to OTP API format
 * and represent a valid date.
 *
 * @param {String} dateString - Date string in OTP API format (yyyy-MM-dd)
 * @param {String} timeString - Time string in OTP API format (HH:mm)
 * @returns {Boolean} True if the date and time strings form a valid date, false otherwise
 */
export const isValidOtpDateAndTime = (dateString, timeString) => {
  if (!dateString?.trim() || !timeString?.trim()) return false;

  try {
    const parsedDate = parse(
      dateString + DATE_AND_TIME_SEPARATOR + timeString,
      OTP_DATETIME_FORMAT,
      new Date()
    );
    return isValid(parsedDate);
  } catch (e) {
    // Handle any parsing errors
    console.warn('Error parsing date:', e);
    return false;
  }
};
